// Análisis del XML a un árbol simple que conserva el orden de los hijos. Ver docs/theory/ork-format.md.
import { XMLParser } from 'fast-xml-parser';

export interface El {
  readonly tag: string;
  readonly attrs: Readonly<Record<string, string>>;
  readonly children: readonly El[];
  /** Texto directo del elemento, recortado. */
  readonly text: string;
}

export type XmlResult = { readonly ok: true; readonly root: El } | { readonly ok: false; readonly error: string };

const MAX_DEPTH = 100;
const MAX_NODES = 2_000_000;

type RawNode = Record<string, unknown>;

class TooDeep extends Error {}

function convert(node: RawNode, state: { nodes: number }, depth: number): El | undefined {
  if (depth > MAX_DEPTH) throw new TooDeep('El XML está demasiado anidado');
  const tag = Object.keys(node).find((k) => k !== ':@' && k !== '#text');
  if (tag === undefined) return undefined;
  if (++state.nodes > MAX_NODES) throw new TooDeep('El XML tiene demasiados elementos');
  const rawAttrs = (node[':@'] ?? {}) as Record<string, unknown>;
  const attrs: Record<string, string> = {};
  for (const [k, v] of Object.entries(rawAttrs)) attrs[k] = String(v);
  const kids = Array.isArray(node[tag]) ? (node[tag] as RawNode[]) : [];
  const children: El[] = [];
  let text = '';
  for (const k of kids) {
    if (k['#text'] !== undefined) text += String(k['#text']);
    else {
      const el = convert(k, state, depth + 1);
      if (el !== undefined) children.push(el);
    }
  }
  return { tag, attrs, children, text: text.trim() };
}

/** Analiza el XML. Rechaza DOCTYPE y entidades (XXE y expansión de entidades). */
export function parseXml(xml: string): XmlResult {
  if (/<!DOCTYPE|<!ENTITY/i.test(xml)) return { ok: false, error: 'El XML contiene DOCTYPE o entidades, que no se permiten' };
  let raw: unknown;
  try {
    raw = new XMLParser({
      preserveOrder: true,
      ignoreAttributes: false,
      attributeNamePrefix: '',
      parseTagValue: false,
      parseAttributeValue: false,
      trimValues: true,
      processEntities: true,
    }).parse(xml, true);
  } catch {
    return { ok: false, error: 'El archivo no es un XML válido' };
  }
  if (!Array.isArray(raw)) return { ok: false, error: 'El archivo no es un XML válido' };
  const state = { nodes: 0 };
  try {
    for (const n of raw as RawNode[]) {
      const el = convert(n, state, 0);
      if (el !== undefined && el.tag !== '?xml') return { ok: true, root: el };
    }
  } catch (e) {
    if (e instanceof TooDeep) return { ok: false, error: e.message };
    throw e;
  }
  return { ok: false, error: 'El XML no tiene elemento raíz' };
}

export const child = (e: El | undefined, tag: string): El | undefined => e?.children.find((c) => c.tag === tag);
export const childrenOf = (e: El | undefined, tag: string): El[] => (e === undefined ? [] : e.children.filter((c) => c.tag === tag));
export const textOf = (e: El | undefined, tag: string): string | undefined => child(e, tag)?.text;

export const AUTO = 'auto' as const;
export type NumOrAuto = number | typeof AUTO | undefined;

/**
 * Número del hijo `tag`. Los valores automáticos se guardan como `auto <valor calculado>`: se usa ese
 * valor si existe y, si no, se devuelve `auto` para resolverlo por los vecinos. Texto no numérico o
 * ausente da `undefined`.
 */
export function numOf(e: El | undefined, tag: string): NumOrAuto {
  const t = textOf(e, tag);
  if (t === undefined) return undefined;
  const auto = /^auto\b\s*(.*)$/i.exec(t);
  if (auto !== null) {
    const stored = auto[1]!.trim();
    const v = Number(stored);
    return stored !== '' && Number.isFinite(v) ? v : AUTO;
  }
  const v = Number(t);
  return t !== '' && Number.isFinite(v) ? v : undefined;
}

export function countDescendants(e: El): number {
  return e.children.reduce((s, c) => s + 1 + countDescendants(c), 0);
}
